import { TInspectionScoreType, TRESPONSE_LotDetail, TransactionRollListingType } from '@monorepo/types';
import {
  fabricInspectionRollStatusPlainLabel,
  fabricInspectionRollStatusTableCell,
  fabricInspectionScoreHeaderKey,
} from './fabric-roll-status.util';
import { ModifiersZTableModel } from '../table.modifier';
import { TTableCellType, TTableCellTree, TTextCell } from '../table.type';

function transactionRollListingStatusCell(
  status: TRESPONSE_LotDetail['rollInfoList'][number]['status'],
  useTagFlow: boolean,
): TTextCell {
  if (!useTagFlow) {
    return {
      type: 'TEXT_CELL',
      text: fabricInspectionRollStatusPlainLabel(status),
      align: 'left',
    };
  }
  const u = fabricInspectionRollStatusTableCell(status);
  if (u.variant === 'plain') {
    return { type: 'TEXT_CELL', text: u.label, align: 'left' };
  }
  return {
    type: 'TEXT_CELL',
    text: '',
    tag: { tagContent: u.labelKey, tagBgColor: u.tagBgColor },
    align: 'left',
  };
}

const getCommonHeaderConfig = (
  fabricVariantType: TRESPONSE_LotDetail['lotInfo']['fabricVariant'],
  useTagFlow: boolean,
  inspectionScoreType: TInspectionScoreType | null,
  showFinalShadeColumn: boolean,
): TTableCellType[] => {
  const headerConfig: TTableCellType[] = [
    {
      type: 'TEXT_CELL',
      text: 'ROLL NO/SERIAL NO',
    },
    {
      type: 'TEXT_CELL',
      text: 'LENGTH',
    },
  ];
  if (fabricVariantType === 'KNIT')
    headerConfig.push({
      type: 'TEXT_CELL',
      text: 'WEIGHT',
    });
  return [
    ...headerConfig,
    {
      type: 'TEXT_CELL',
      text: 'WIDTH',
    },
    ...(showFinalShadeColumn ? [{ type: 'TEXT_CELL' as const, text: 'FINAL SHADE' }] : []),
    {
      type: 'TEXT_CELL',
      text: useTagFlow ? 'TOTAL DEFECT PTS' : 'POINTS',
    },
    {
      type: 'TEXT_CELL',
      text: fabricInspectionScoreHeaderKey(useTagFlow, inspectionScoreType),
    },
    {
      type: 'TEXT_CELL',
      text: 'STATUS',
    },
  ];
};

const getCommonCells = (
  item: TRESPONSE_LotDetail['rollInfoList'][number],
  fabricVariantType: TRESPONSE_LotDetail['lotInfo']['fabricVariant'],
  useTagFlow: boolean,
  showFinalShadeColumn: boolean,
): TTableCellTree[] => {
  const commonCells: TTableCellTree[] = [
    {
      type: 'TEXT_CELL',
      text: item.rollNo,
    },
    {
      type: 'TEXT_CELL',
      text: item.length,
    },
  ];
  if (fabricVariantType === 'KNIT')
    commonCells.push({
      type: 'TEXT_CELL',
      text: item.weight,
    });
  return [
    ...commonCells,
    {
      type: 'TEXT_CELL',
      text: item.width,
    },
    ...(showFinalShadeColumn ? [{ type: 'TEXT_CELL' as const, text: item.finalShade ?? '-' }] : []),
    {
      type: 'TEXT_CELL',
      text: item.inspectionResult && item.inspectionResult.points !== null ? item.inspectionResult.points : '--',
    },
    {
      type: 'TEXT_CELL',
      text: item.inspectionResult && item.inspectionResult.score !== null ? item.inspectionResult.score : '--',
    },
    transactionRollListingStatusCell(item.status, useTagFlow),
  ];
};

export const FABRIC_TRANSACTION_ROLL_LISTING_MODEL = {
  createModel: (
    items: TRESPONSE_LotDetail['rollInfoList'],
    fabricVariantType: TRESPONSE_LotDetail['lotInfo']['fabricVariant'],
    rollListingType: TransactionRollListingType,
    useTagFlow = false,
    inspectionScoreType: TInspectionScoreType | null = null,
    showFinalShadeColumn: boolean = false,
  ) => {
    if (rollListingType === 'COMMERCIALLY_APPROVE') {
      return ModifiersZTableModel.createTableModel(
        items,
        {
          headerConfig: getCommonHeaderConfig(fabricVariantType, useTagFlow, inspectionScoreType, showFinalShadeColumn),
          select: {
            position: 'END',
            isMulti: true,
            size: 'LARGE',
          },
        },
        (item) => {
          return {
            type: 'ROW',
            rowItem: item,
            isSelected: false,
            cells: [...getCommonCells(item, fabricVariantType, useTagFlow, showFinalShadeColumn)],
          };
        },
      );
    } else {
      return ModifiersZTableModel.createTableModel(
        items,
        {
          headerConfig: [
            ...getCommonHeaderConfig(fabricVariantType, useTagFlow, inspectionScoreType, showFinalShadeColumn),
            {
              type: 'TEXT_CELL',
              text: 'DETAILS',
              align: 'center',
            },
          ],
        },
        (item) => {
          return {
            type: 'ROW',
            rowItem: item,
            isSelected: false,
            cells: [
              ...getCommonCells(item, fabricVariantType, useTagFlow, showFinalShadeColumn),
              {
                type: 'ACTION_CELL',
                actionType: 'VIEW',
              },
            ],
          };
        },
      );
    }
  },
};
